import { styled } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CustomTextInput from '@components/CustomTextInput';
import { getIconsByType, iconTypes } from '@services/icons';

const SearchIcon = getIconsByType(iconTypes.SEARCH);

export const PageShell = styled('div')({
  minHeight: 'calc(100vh - 70px)',
  width: '100%',
  display: 'grid',
  alignContent: 'center',
  padding: '16px 0',
  boxSizing: 'border-box',
});

export const Container = styled('div')(({ theme }) => ({
  maxWidth: 980,
  width: '100%',
  margin: '0 auto',
  padding: '24px',
  borderRadius: '14px',
  background: `linear-gradient(180deg, ${theme.palette.novotalk.$backgroundColor} 0%, ${theme.palette.novotalk.$backgroundColorMedium} 100%)`,
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
  '@media (max-width: 900px)': {
    padding: '16px',
    borderRadius: '10px',
  },
}));

export const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '18px',
});

export const HeaderActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

export const SummaryBadge = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 32,
  padding: '0 12px',
  borderRadius: 999,
  color: theme.palette.novotalk.$textColorDarkBlue,
  background: theme.palette.novotalk.$resourcesTableBgColorSelected,
  border: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
}));

export const SearchContainer = styled('div')({
  marginBottom: 12,
  display: 'flex',
  justifyContent: 'flex-start',
});

export const SearchInput = styled(CustomTextInput)(
  {
    minHeight: 38,
    maxHeight: 38,
    borderRadius: 5,
    outline: 'none',
    width: 300,
    maxWidth: 300,
  },
  ({ theme }) => ({
    backgroundColor: theme.palette.novotalk.$resourcesInputBgColor,
    borderColor: theme.palette.novotalk.$resourcesInputBgColor,
    '@media (max-width: 700px)': {
      maxWidth: '100%',
      width: '100%',
    },
  }),
);

export const SearchInputIcon = styled(SearchIcon)(
  {
    fontSize: 18,
    marginRight: 10,
  },
  ({ theme }) => ({
    color: theme.palette.novotalk.$textColorLightGray,
  }),
);

export const CreateRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '10px',
  marginBottom: '18px',
  alignItems: 'center',
  padding: '12px',
  borderRadius: '10px',
  background: theme.palette.background.default,
  '@media (max-width: 640px)': {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

export const Input = styled('input')(({ theme }) => ({
  flex: '0 1 520px',
  width: '100%',
  maxWidth: 520,
  minHeight: 46,
  padding: '10px 14px',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.novotalk.$inputBorderColor}`,
  fontSize: '15px',
  color: theme.palette.novotalk.$textColorDarkBlue,
  background: theme.palette.novotalk.$backgroundColor,
  outline: 'none',
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
  '&:focus': {
    borderColor: theme.palette.novotalk.$activeColor,
    boxShadow: `0 0 0 3px ${theme.palette.novotalk.$borderColorLightBlue}`,
  },
  '&::placeholder': {
    color: theme.palette.novotalk.$textColorDisabledLight,
  },
}));

export const AddIconButton = styled(IconButton)(({ theme }) => ({
  width: 40,
  minWidth: 40,
  height: 40,
  borderRadius: '10px',
  color: theme.palette.novotalk.$textColorReversed,
  background: theme.palette.novotalk.$ctaButtonColor,
  '&:hover': {
    background: theme.palette.novotalk.$ctaButtonColorHover,
  },
  '&:active': {
    background: theme.palette.novotalk.$ctaButtonColorActive,
  },
  '&:disabled': {
    opacity: 0.55,
  },
  '@media (max-width: 640px)': {
    width: 40,
    minWidth: 40,
  },
}));

export const SaveIconButton = styled(IconButton)(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: '9px',
  color: theme.palette.novotalk.$actionButtonColor,
  background: theme.palette.novotalk.$selectEnabledBgColorHover,
  '&:hover': {
    background: theme.palette.novotalk.$selectEnabledBgColorActive,
  },
  '&:disabled': {
    opacity: 0.55,
  },
}));

export const EditIconButton = SaveIconButton;

export const CancelIconButton = styled(IconButton)(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: '9px',
  color: theme.palette.novotalk.$textColorDarkGray,
  background: theme.palette.novotalk.$backgroundColorMedium,
  '&:hover': {
    background: theme.palette.novotalk.$backgroundColorMediumHover,
  },
  '&:disabled': {
    opacity: 0.55,
  },
}));

export const DeleteIconButton = styled(IconButton)(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: '9px',
  color: theme.palette.novotalk.$warningColor,
  background: theme.palette.novotalk.$statusFailedBackgroundColor,
  '&:hover': {
    background: 'rgba(240,137,26,0.22)',
  },
  '&:disabled': {
    opacity: 0.55,
  },
}));

export const TableWrapper = styled('div')({
  borderRadius: '10px',
  overflow: 'auto',
  height: '70vh',
  minHeight: '70vh',
  '@media (max-width: 900px)': {
    height: '64vh',
    minHeight: '64vh',
  },
});

export const Table = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
});

export const Th = styled('th')(({ theme }) => ({
  textAlign: 'left',
  borderBottom: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
  padding: '12px 10px',
  background: theme.palette.novotalk.$backgroundColor,
  color: theme.palette.novotalk.$textColorDisabledLight,
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  '&:nth-of-type(2)': {
    width: 90,
  },
  '&:nth-of-type(3)': {
    width: 90,
  },
  '&:nth-of-type(4)': {
    width: 130,
  },
  '&[clickable]': {
    cursor: 'pointer',
    userSelect: 'none',
  },
  '&[clickable] > span': {
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
  },
}));

export const Td = styled('td')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
  padding: '10px',
  verticalAlign: 'middle',
}));

export const Row = styled('tr')(({ theme }) => ({
  background: theme.palette.novotalk.$backgroundColor,
  '&:hover': {
    background: theme.palette.novotalk.$resourcesTableBgColorHover,
  },
  '&:active': {
    background: theme.palette.novotalk.$resourcesTableBgColorActive,
  },
}));

export const DraftRow = styled(Row)(({ theme }) => ({
  background: theme.palette.novotalk.$resourcesTableBgColorSelected,
}));

export const ActionsRow = styled('div')({
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-start',
  '@media (max-width: 640px)': {
    flexDirection: 'column',
  },
});
